import { EggSvg } from './EggSvg';
import { ProgressRing } from './ProgressRing';
import { NestSvg } from './NestSvg';
import { formatCountdown } from '@/constants/rewards';
import type { EggView, EggTierId } from '@/types/rewards';

interface HatcherySlotProps {
  egg?: EggView;
  progress?: number;
  onHatch?: (eggId: string) => void;
  hatching?: boolean;
}

export function HatcherySlot({ egg, progress = 0, onHatch, hatching }: HatcherySlotProps) {
  if (!egg) {
    return (
      <div
        className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-or-slate-200 p-6 min-h-[280px]"
        style={{ backgroundColor: '#FAF6EE' }}
      >
        <NestSvg size={80} />
        <p className="mt-3 text-sm text-or-slate-400">Empty nest</p>
      </div>
    );
  }

  const ready = progress >= 1;
  const remaining = Math.max(0, egg.duration - (Date.now() - new Date(egg.startedAt).getTime()));

  return (
    <div
      className="flex flex-col items-center rounded-2xl border-2 p-5 min-h-[280px] transition-all"
      style={
        ready
          ? {
              background: 'linear-gradient(180deg, #FFF1DC 0%, #FFE4BD 100%)',
              borderColor: '#E89149',
              boxShadow: '0 0 20px rgba(232, 145, 73, 0.3)',
            }
          : {
              background: 'linear-gradient(180deg, #FAF8F4 0%, #FFFFFF 100%)',
              borderColor: '#E8DFD0',
            }
      }
    >
      {/* Egg + ring */}
      <div className="relative flex items-center justify-center w-[156px] h-[156px] mb-3">
        <div className="absolute inset-0 flex items-center justify-center">
          <ProgressRing progress={progress} size={148} strokeWidth={6} ready={ready} />
        </div>
        <div className={ready ? 'animate-egg-shake' : 'animate-egg-bob'}>
          <EggSvg tierId={egg.tierId as EggTierId} size={72} />
        </div>
      </div>

      {/* Tier name */}
      <p className="text-base font-display font-bold text-stone-800 mb-1">{egg.tierName}</p>

      {/* Status label */}
      <p className="text-sm font-medium mb-4" style={{ color: ready ? '#C06A1A' : '#94A3B8' }}>
        {ready ? '\u2726 Ready to hatch!' : `Hatching in ${formatCountdown(remaining)}`}
      </p>

      {/* Action button */}
      {ready ? (
        <button
          onClick={() => onHatch?.(egg.id)}
          disabled={hatching}
          className="w-full rounded-xl py-2.5 text-sm font-semibold text-white transition-colors disabled:opacity-50"
          style={{
            background: 'linear-gradient(180deg, #F09D51 0%, #D97B2B 100%)',
            boxShadow: '0 2px 8px rgba(217, 123, 43, 0.4)',
          }}
        >
          {hatching ? 'Cracking...' : 'Crack it open \u2726'}
        </button>
      ) : (
        <button
          disabled
          className="w-full rounded-xl border border-or-slate-200 bg-or-cream-50 py-2.5 text-sm font-medium text-or-slate-400 cursor-not-allowed"
        >
          Incubating&hellip;
        </button>
      )}
    </div>
  );
}
